<template>
  <div class="signin">
    <!-- the living side: an animated forest at dusk with the platform's live figures -->
    <section class="si-scene" aria-hidden="true">
      <div class="si-aurora a1"></div>
      <div class="si-aurora a2"></div>
      <div class="si-aurora a3"></div>
      <span v-for="n in 22" :key="'s' + n" class="si-star" :style="starStyle(n)"></span>
      <span v-for="n in 12" :key="'l' + n" class="si-leaf" :style="leafStyle(n)"><i class="fas fa-leaf"></i></span>

      <svg class="si-forest" viewBox="0 0 1200 320" preserveAspectRatio="xMidYMax slice">
        <defs>
          <linearGradient id="si-far" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#1d5a43"/><stop offset="1" stop-color="#0d2a1f"/></linearGradient>
          <linearGradient id="si-near" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#0f3a28"/><stop offset="1" stop-color="#06150e"/></linearGradient>
        </defs>
        <path class="si-hills" d="M0 210 C 150 150 260 190 380 160 S 620 120 760 170 S 1010 130 1200 180 L1200 320 L0 320 Z" fill="url(#si-far)"/>
        <g class="si-trees far">
          <polygon v-for="t in farTrees" :key="'f' + t.x" :points="pine(t.x, 230, t.h, t.w)" :style="{ animationDelay: t.d + 's' }"/>
        </g>
        <g class="si-trees near">
          <polygon v-for="t in nearTrees" :key="'n' + t.x" :points="pine(t.x, 320, t.h, t.w)" :style="{ animationDelay: t.d + 's' }"/>
        </g>
      </svg>

      <div class="si-scene-content">
        <img class="si-logo" src="@/assets/logo.png" alt="">
        <h1>Every tree counts.<br><span>Every tonne is certified.</span></h1>
        <p>Real forests, measured from the ground and recorded on the blockchain. Sign in to follow yours.</p>
        <div class="si-figures">
          <div class="si-fig">
            <strong><animated-number :value="figures.trees" locale="en-GB" :duration="2200"/></strong>
            <span class="si-fig-label">trees growing</span>
          </div>
          <div class="si-fig">
            <strong><animated-number :value="figures.forests" locale="en-GB" :duration="2200"/></strong>
            <span class="si-fig-label">forests</span>
          </div>
          <div class="si-fig">
            <strong><animated-number :value="figures.yearly" locale="en-GB" :duration="2200"/> t</strong>
            <span class="si-fig-label">CO2 captured a year</span>
          </div>
        </div>
      </div>
    </section>

    <!-- the form -->
    <section class="si-panel">
      <div class="si-card" :class="{ shake }">
        <router-link to="/" class="si-back"><i class="fas fa-arrow-left"></i> Back to home</router-link>

        <div class="si-head">
          <h2>{{ mode === 'login' ? 'Welcome back' : 'Create your account' }}</h2>
          <p>{{ mode === 'login' ? 'Sign in to see your forests and your impact.' : 'Join Zerticarbon and follow the forests you support.' }}</p>
        </div>

        <div class="si-tabs" role="tablist">
          <span class="si-tabs-pill" :class="{ right: mode === 'register' }"></span>
          <button type="button" role="tab" :aria-selected="mode === 'login'" :class="{ on: mode === 'login' }" @click="setMode('login')">Sign in</button>
          <button type="button" role="tab" :aria-selected="mode === 'register'" :class="{ on: mode === 'register' }" @click="setMode('register')">Create account</button>
        </div>

        <transition name="si-swap" mode="out-in">
          <form v-if="mode === 'login'" key="login" class="si-form" @submit.prevent="onSubmitLogin" novalidate>
            <label class="si-field">
              <i class="fas fa-envelope"></i>
              <input v-model.trim="login.email" type="email" autocomplete="email" placeholder=" " required>
              <span>Email</span>
            </label>
            <label class="si-field">
              <i class="fas fa-lock"></i>
              <input v-model="login.password" :type="showPassword ? 'text' : 'password'" autocomplete="current-password" placeholder=" " required>
              <span>Password</span>
              <button type="button" class="si-eye" @click="showPassword = !showPassword" :aria-label="showPassword ? 'Hide password' : 'Show password'"><i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i></button>
            </label>
            <transition name="si-msg"><div v-if="error" class="si-error"><i class="fas fa-exclamation-circle"></i> {{ error }}</div></transition>
            <button type="submit" class="si-submit" :disabled="busy">
              <span v-if="busy" class="si-spinner"></span>
              <template v-else>Sign in <i class="fas fa-arrow-right"></i></template>
            </button>
          </form>

          <form v-else key="register" class="si-form" @submit.prevent="onSubmitRegister" novalidate>
            <label class="si-field">
              <i class="fas fa-user"></i>
              <input v-model.trim="register.name" type="text" autocomplete="name" placeholder=" " required>
              <span>Name</span>
            </label>
            <label class="si-field">
              <i class="fas fa-envelope"></i>
              <input v-model.trim="register.email" type="email" autocomplete="email" placeholder=" " required>
              <span>Email</span>
            </label>
            <label class="si-field">
              <i class="fas fa-envelope-open"></i>
              <input v-model.trim="register.email2" type="email" autocomplete="email" placeholder=" " required>
              <span>Repeat email</span>
            </label>
            <div class="si-row">
              <label class="si-field">
                <i class="fas fa-lock"></i>
                <input v-model="register.password" :type="showPassword ? 'text' : 'password'" autocomplete="new-password" placeholder=" " required>
                <span>Password</span>
              </label>
              <label class="si-field">
                <i class="fas fa-lock"></i>
                <input v-model="register.password2" :type="showPassword ? 'text' : 'password'" autocomplete="new-password" placeholder=" " required>
                <span>Repeat it</span>
                <button type="button" class="si-eye" @click="showPassword = !showPassword" :aria-label="showPassword ? 'Hide passwords' : 'Show passwords'"><i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i></button>
              </label>
            </div>
            <transition name="si-msg"><div v-if="error" class="si-error"><i class="fas fa-exclamation-circle"></i> {{ error }}</div></transition>
            <button type="submit" class="si-submit" :disabled="busy">
              <span v-if="busy" class="si-spinner"></span>
              <template v-else>Create my account <i class="fas fa-arrow-right"></i></template>
            </button>
          </form>
        </transition>

        <div class="si-or"><span>or</span></div>

        <button type="button" class="si-wallet" :disabled="busy" @click="loginWithWallet">
          <span class="si-wallet-icon"><i class="fas fa-wallet"></i></span>
          <span class="si-wallet-text">
            <strong>Continue with your wallet</strong>
            <small>MetaMask or any Ethereum wallet: sign a message, no transaction.</small>
          </span>
          <i class="fas fa-chevron-right"></i>
        </button>

        <p class="si-legal">Protected by reCAPTCHA. Your data stays with Zerticarbon.</p>
      </div>
    </section>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { VueReCaptcha } from 'vue-recaptcha-v3';
import Login from '../api/Login';
import Stats from '../api/Stats';
import TokenStorage from '../utils/TokenStorage';
import AnimatedNumber from '../components/Stats/AnimatedNumber.vue';

Vue.use(VueReCaptcha, { siteKey: '6Lf_PGghAAAAAC_U5PX_G6jeg9gO3YxnU2mT6WR_' });

const rand = (n, k) => ((Math.sin(n * 12.9898 + k * 78.233) * 43758.5453) % 1 + 1) % 1;

/**
 * The sign-in page: email + password (with reCAPTCHA), a new account, or the wallet (a signed message). Same flows and
 * API calls as before; after signing in the visitor lands on their dashboard.
 */
export default {
  name: 'Dashboard',
  components: { AnimatedNumber },
  data() {
    return {
      mode: this.$route.query.register !== undefined ? 'register' : 'login',
      login: { email: '', password: '' },
      register: { name: '', email: '', email2: '', password: '', password2: '' },
      showPassword: false,
      busy: false,
      error: '',
      shake: false,
      figures: { trees: 0, forests: 0, yearly: 0 },
    };
  },
  computed: {
    ...mapGetters('user', ['isWeb2UserConnected']),
    ...mapGetters('accounts', ['getWeb3', 'getActiveAccount']),
    farTrees() { return Array.from({ length: 34 }, (_, i) => ({ x: i * 36 + rand(i, 1) * 20, h: 40 + rand(i, 2) * 50, w: 12 + rand(i, 3) * 8, d: 0.2 + rand(i, 4) * 1.4 })); },
    nearTrees() { return Array.from({ length: 22 }, (_, i) => ({ x: i * 56 + rand(i, 5) * 30, h: 90 + rand(i, 6) * 90, w: 22 + rand(i, 7) * 14, d: 0.5 + rand(i, 8) * 1.6 })); },
  },
  watch: {
    isWeb2UserConnected(v) { if (v) this.$router.replace('/'); },
  },
  created() {
    if (this.isWeb2UserConnected) { this.$router.replace('/'); return; }
    Stats.getOverview().then((r) => {
      const o = r.data || {};
      this.figures = { trees: Number(o.totalTrees) || 0, forests: Number(o.areasQty) || 0, yearly: Math.round(Number(o.totalTheoreticalYearlyIncrement) || 0) };
    }).catch(() => {});
  },
  methods: {
    ...mapActions('user', ['connectAccount']),
    ...mapActions('accounts', ['connectWeb3Modal']),

    setMode(m) { this.mode = m; this.error = ''; },
    fail(message) {
      this.error = message;
      this.shake = false;
      this.$nextTick(() => { this.shake = true; setTimeout(() => { this.shake = false; }, 600); });
    },
    welcome(response, text) {
      TokenStorage.set(response.data.jwt);
      this.connectAccount(response.data.user);
      this.$toasted.show(text, { type: 'success', duration: 4000, theme: 'bubble', position: 'top-center' });
      this.$router.replace('/');
    },

    async onSubmitLogin() {
      this.error = '';
      if (!this.login.email || !this.login.password) return this.fail('Enter your email and your password.');
      this.busy = true;
      try {
        await this.$recaptchaLoaded();
        const token = await this.$recaptcha('login');
        const response = await Login.postLogin(token, { identifier: this.login.email, password: this.login.password });
        this.welcome(response, 'Welcome back!');
      } catch (e) {
        this.fail('The email or the password is not correct.');
      } finally {
        this.busy = false;
      }
    },

    async onSubmitRegister() {
      this.error = '';
      const r = this.register;
      if (!r.name || !r.email || !r.password) return this.fail('Fill in your name, your email and a password.');
      if (r.email !== r.email2) return this.fail('The two emails are not the same.');
      if (r.password !== r.password2) return this.fail('The two passwords are not the same.');
      if (r.password.length < 6) return this.fail('The password needs at least 6 characters.');
      this.busy = true;
      try {
        await this.$recaptchaLoaded();
        const token = await this.$recaptcha('login');
        const response = await Login.postRegister(token, { name: r.name, email: r.email, username: r.email, password: r.password });
        this.welcome(response, 'Your account is ready. Welcome!');
      } catch (e) {
        this.fail('That email cannot be used. Is it already registered?');
      } finally {
        this.busy = false;
      }
    },

    async loginWithWallet() {
      this.error = '';
      this.busy = true;
      try {
        await this.connectWeb3Modal();
        if (!this.getWeb3 || !this.getActiveAccount) throw new Error('no wallet');
        const signature = await this.getWeb3.eth.personal.sign('Confirm your signature to log in to Zerticarbon.\nEnsure you are on the app.zerticarbon.com domain.', this.getActiveAccount);
        const response = await Login.postLoginWallet({ address: window.ethereum.selectedAddress, signature });
        if (response.data.jwt) TokenStorage.set(response.data.jwt);
        this.connectAccount(response.data.user);
        this.$toasted.show('Welcome!', { type: 'success', duration: 4000, theme: 'bubble', position: 'top-center' });
        this.$router.replace('/');
      } catch (e) {
        this.fail('The wallet could not sign in. Is a wallet installed and unlocked?');
      } finally {
        this.busy = false;
      }
    },

    pine(x, base, h, w) {
      // a three-tier pine silhouette
      const t = (f) => base - h * f;
      return [
        [x, t(1)], [x + w * 0.45, t(0.62)], [x + w * 0.25, t(0.62)], [x + w * 0.7, t(0.3)], [x + w * 0.4, t(0.3)],
        [x + w, base], [x - w, base], [x - w * 0.4, t(0.3)], [x - w * 0.7, t(0.3)], [x - w * 0.25, t(0.62)], [x - w * 0.45, t(0.62)],
      ].map((p) => p.join(',')).join(' ');
    },
    starStyle(n) {
      return { left: `${rand(n, 11) * 100}%`, top: `${rand(n, 12) * 55}%`, animationDelay: `${rand(n, 13) * 4}s`, transform: `scale(${0.5 + rand(n, 14)})` };
    },
    leafStyle(n) {
      return { left: `${rand(n, 21) * 100}%`, animationDuration: `${10 + rand(n, 22) * 10}s`, animationDelay: `-${rand(n, 23) * 14}s`, fontSize: `${0.6 + rand(n, 24) * 0.7}rem`, opacity: 0.2 + rand(n, 25) * 0.35 };
    },
  },
};
</script>

<style scoped lang="scss">
.signin {
  min-height: 100vh;
  display: flex;
  flex-direction: row; // side by side on wide screens (the app's full-screen wrapper stacks its pages by default)
  background: var(--bg-primary);
}

/* ---------------------------------------------------------------- the scene */
.si-scene {
  position: relative;
  flex: 1 1 56%;
  overflow: hidden;
  isolation: isolate;
  color: #fff;
  background: radial-gradient(120% 90% at 20% 0%, #1d6b52 0%, #0d3426 45%, #061a12 100%);
}

.si-aurora {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.5;
  mix-blend-mode: screen;
  z-index: -1;
}
.a1 { width: 55%; height: 55%; left: -10%; top: -15%; background: #3ddc97; animation: si-drift1 18s ease-in-out infinite alternate; }
.a2 { width: 50%; height: 50%; right: -10%; top: 5%; background: #2fd0c4; animation: si-drift2 22s ease-in-out infinite alternate; }
.a3 { width: 40%; height: 40%; left: 30%; top: 25%; background: #9ed77f; opacity: 0.25; animation: si-drift1 26s ease-in-out infinite alternate-reverse; }
@keyframes si-drift1 { to { transform: translate(20%, 25%) scale(1.15); } }
@keyframes si-drift2 { to { transform: translate(-25%, 15%) scale(0.9); } }

.si-star {
  position: absolute;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #fff;
  z-index: -1;
  animation: si-twinkle 3.6s ease-in-out infinite;
}
@keyframes si-twinkle { 0%, 100% { opacity: 0.15; } 50% { opacity: 0.9; } }

.si-leaf {
  position: absolute;
  bottom: -20px;
  color: #c9f7b3;
  z-index: 2;
  animation: si-rise linear infinite;
  pointer-events: none;
}
@keyframes si-rise {
  0% { transform: translateY(0) rotate(0); }
  50% { transform: translateY(-45vh) translateX(24px) rotate(170deg); }
  100% { transform: translateY(-95vh) translateX(-12px) rotate(340deg); opacity: 0; }
}

.si-forest {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 42%;
  z-index: 0;
}

.si-hills { animation: si-rise-in 1.4s cubic-bezier(.2, .8, .2, 1) both; }
// each tree grows from the ground; the whole row sways gently (two animations on one element would fight over transform)
.si-trees {
  transform-box: fill-box;
  transform-origin: 50% 100%;
  animation: si-sway 6s ease-in-out infinite alternate;
  &.far { animation-duration: 8s; }
}
.si-trees polygon {
  transform-box: fill-box;
  transform-origin: 50% 100%;
  animation: si-grow 1.6s cubic-bezier(.2, .8, .2, 1) both;
}
.si-trees.far polygon { fill: #174b36; }
.si-trees.near polygon { fill: url(#si-near); }
@keyframes si-grow { from { transform: scaleY(0); } to { transform: scaleY(1); } }
@keyframes si-sway { from { transform: skewX(-1.2deg); } to { transform: skewX(1.2deg); } }
@keyframes si-rise-in { from { transform: translateY(40px); opacity: 0; } to { transform: none; opacity: 1; } }

.si-scene-content {
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: clamp(32px, 6vw, 80px);
  padding-bottom: 32vh;
  max-width: 640px;
  animation: si-up 0.9s cubic-bezier(.2, .8, .2, 1) both;

  h1 {
    font-family: 'Sora', 'Plus Jakarta Sans', sans-serif;
    font-weight: 800;
    font-size: clamp(2rem, 3.6vw, 3.2rem);
    line-height: 1.08;
    letter-spacing: -0.02em;
    margin: 22px 0 14px;
    color: #fff;

    span {
      background: linear-gradient(90deg, #9ed77f, #3ddc97, #2fd0c4, #9ed77f);
      background-size: 200% auto;
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
      animation: si-shine 7s linear infinite;
    }
  }

  p { font-size: 1.02rem; color: rgba(255, 255, 255, 0.75); max-width: 480px; margin: 0; }
}
@keyframes si-shine { to { background-position: 200% center; } }
@keyframes si-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: none; } }

.si-logo { width: 150px; filter: drop-shadow(0 6px 24px rgba(61, 220, 151, 0.35)); }

.si-figures {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 28px;
}

.si-fig {
  padding: 12px 16px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(10px);
  animation: si-up 0.9s cubic-bezier(.2, .8, .2, 1) both;

  &:nth-child(2) { animation-delay: 0.12s; }
  &:nth-child(3) { animation-delay: 0.24s; }

  strong { display: block; font-family: 'Sora', sans-serif; font-size: 1.25rem; font-variant-numeric: tabular-nums; }
  .si-fig-label { font-size: 0.74rem; color: rgba(255, 255, 255, 0.7); }
}

/* ---------------------------------------------------------------- the form */
.si-panel {
  flex: 1 1 44%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  background: var(--bg-primary-gradient, var(--bg-primary));
}

.si-card {
  width: min(440px, 100%);
  padding: 30px 30px 24px;
  border-radius: 26px;
  background: var(--card-bg);
  border: 1px solid var(--border-subtle, rgba(0, 0, 0, 0.06));
  box-shadow: var(--shadow-lg);
  animation: si-card-in 0.8s cubic-bezier(.2, .8, .2, 1) both;

  &.shake { animation: si-shake 0.5s cubic-bezier(.36, .07, .19, .97); }
}
@keyframes si-card-in { from { opacity: 0; transform: translateY(24px) scale(0.98); } to { opacity: 1; transform: none; } }
@keyframes si-shake { 20%, 60% { transform: translateX(-8px); } 40%, 80% { transform: translateX(8px); } }

.si-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--text-muted);
  text-decoration: none;
  &:hover { color: var(--accent-green); text-decoration: none; }
}

.si-head {
  margin: 18px 0 18px;
  h2 { margin: 0; font-weight: 800; font-size: 1.6rem; color: var(--text-heading); }
  p { margin: 4px 0 0; color: var(--text-muted); font-size: 0.9rem; }
}

.si-tabs {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 4px;
  border-radius: 14px;
  background: var(--bg-secondary);
  margin-bottom: 18px;

  button {
    position: relative;
    z-index: 1;
    border: none;
    background: transparent;
    padding: 9px 10px;
    border-radius: 10px;
    font-weight: 700;
    font-size: 0.88rem;
    color: var(--text-muted);
    cursor: pointer;
    transition: color 0.3s;
    &.on { color: var(--accent-green); }
  }
}

.si-tabs-pill {
  position: absolute;
  top: 4px;
  bottom: 4px;
  left: 4px;
  width: calc(50% - 4px);
  border-radius: 10px;
  background: var(--card-bg);
  border: 1px solid rgba(var(--accent-green-rgb), 0.3);
  box-shadow: 0 6px 18px -8px rgba(var(--accent-green-rgb), 0.55);
  transition: transform 0.4s cubic-bezier(.2, .8, .2, 1);
  &.right { transform: translateX(100%); }
}

.si-form { display: flex; flex-direction: column; gap: 12px; }
.si-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }

.si-field {
  position: relative;
  display: block;
  margin: 0;

  > i {
    position: absolute;
    left: 15px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-muted);
    font-size: 0.85rem;
    transition: color 0.25s;
    pointer-events: none;
  }

  input {
    width: 100%;
    height: 54px;
    padding: 18px 42px 4px 42px;
    border-radius: 14px;
    border: 1.5px solid var(--border-color);
    background: var(--bg-secondary);
    color: var(--text-heading);
    font-size: 0.95rem;
    outline: none;
    transition: border-color 0.25s, box-shadow 0.25s, background 0.25s;

    &:focus {
      border-color: var(--accent-green);
      background: var(--card-bg);
      box-shadow: 0 0 0 4px rgba(var(--accent-green-rgb), 0.15);
    }
  }

  > span {
    position: absolute;
    left: 42px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.92rem;
    color: var(--text-muted);
    pointer-events: none;
    transition: top 0.25s cubic-bezier(.2, .8, .2, 1), font-size 0.25s, color 0.25s;
  }

  input:focus ~ span,
  input:not(:placeholder-shown) ~ span { top: 15px; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.03em; color: var(--accent-green); }

  &:focus-within > i { color: var(--accent-green); }
}

.si-eye {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  &:hover { color: var(--accent-green); background: rgba(var(--accent-green-rgb), 0.08); }
}

.si-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 12px;
  font-size: 0.84rem;
  color: #c0392b;
  background: rgba(217, 83, 79, 0.1);
  border: 1px solid rgba(217, 83, 79, 0.25);
}

.si-submit {
  position: relative;
  height: 52px;
  border: none;
  border-radius: 14px;
  color: #fff;
  font-weight: 800;
  font-size: 0.98rem;
  background: var(--accent-gradient);
  background-size: 160% 100%;
  box-shadow: var(--shadow-glow);
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.25s cubic-bezier(.2, .8, .2, 1), background-position 0.5s, box-shadow 0.25s;

  i { margin-left: 6px; transition: transform 0.25s; }
  &:hover:not(:disabled) { transform: translateY(-2px); background-position: 100% 0; i { transform: translateX(4px); } }
  &:disabled { opacity: 0.8; cursor: progress; }
}

.si-spinner {
  display: inline-block;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  animation: si-spin 0.8s linear infinite;
  vertical-align: middle;
}
@keyframes si-spin { to { transform: rotate(360deg); } }

.si-or {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 18px 0;
  color: var(--text-muted);
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;

  &::before, &::after { content: ''; flex: 1; height: 1px; background: var(--border-color); }
}

.si-wallet {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 16px;
  border: 1.5px solid var(--border-color);
  background: transparent;
  color: var(--text-main);
  text-align: left;
  cursor: pointer;
  transition: border-color 0.25s, transform 0.25s, background 0.25s;

  &:hover:not(:disabled) { border-color: var(--accent-green); background: rgba(var(--accent-green-rgb), 0.06); transform: translateY(-1px); }
  > i { margin-left: auto; color: var(--text-muted); }
}

.si-wallet-icon {
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(135deg, #f6a04d, #e2761b);
  box-shadow: 0 6px 16px -6px #e2761b;
}

.si-wallet-text {
  display: flex;
  flex-direction: column;
  strong { font-size: 0.9rem; color: var(--text-heading); }
  small { font-size: 0.74rem; color: var(--text-muted); }
}

.si-legal { margin: 16px 0 0; text-align: center; font-size: 0.7rem; color: var(--text-muted); }

.si-swap-enter-active, .si-swap-leave-active { transition: opacity 0.25s, transform 0.25s; }
.si-swap-enter { opacity: 0; transform: translateX(14px); }
.si-swap-leave-to { opacity: 0; transform: translateX(-14px); }
.si-msg-enter-active, .si-msg-leave-active { transition: opacity 0.25s, transform 0.25s; }
.si-msg-enter, .si-msg-leave-to { opacity: 0; transform: translateY(-4px); }

/* ---------------------------------------------------------------- phones and tablets: the scene becomes a band */
@media (max-width: 991px) {
  .signin { flex-direction: column; }
  .si-scene { flex: 0 0 auto; min-height: 300px; }
  .si-scene-content {
    padding: 28px 22px 110px;
    align-items: center;
    text-align: center;
    max-width: none;
    h1 { font-size: clamp(1.6rem, 6vw, 2.2rem); margin: 14px 0 8px; }
    p { font-size: 0.9rem; }
  }
  .si-logo { width: 120px; }
  .si-figures { justify-content: center; margin-top: 18px; }
  .si-fig { padding: 9px 12px; strong { font-size: 1.05rem; } }
  .si-forest { height: 120px; }
  .si-panel { padding: 0 16px 32px; margin-top: -60px; position: relative; z-index: 2; background: transparent; }
}

@media (max-width: 420px) {
  .si-row { grid-template-columns: 1fr; }
  .si-card { padding: 24px 18px 20px; }
  .si-fig:nth-child(3) { display: none; }
}
</style>
